import { Check, FontIcon, Icon, IIconStyles, Spinner, SpinnerSize, Text, TooltipDelay, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import dayjs from "dayjs";
import React from "react";
import { useSelector } from "react-redux";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { ComponentTypes } from "../../../../../config/config";
import { OneNoteProgId, SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../../../../config/constants";
import { IFile } from "../../../../../interfaces/IFile";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { RootState } from "../../../redux/storeMain";
import { PiiTooltip } from "../../Tooltips/PiiTooltip";
import { getItemCustomSelect } from "../../../../../services/GraphService";
import authModule from "../../../../../../../utilities/AuthModule";
import { getExtension } from "../../../../helpers/stringHelper";
import { MoreVertical20Regular } from "../../SvgIcons/MoreVertical20Regular";

const iconStyles: IIconStyles = {
    root: {
        // paddingTop: 3
    }
}

export interface IFileviewProps {
    item?: IFile,
    exchangeItem?: any,
    isMobile: boolean,
    tenantEnablePII: boolean;
    selectFile?: (selectedFile: IFile) => void;
    selectExchangeItem?: (selectedItem: any) => void;
    openFileOptions?: (selectedFile: IFile) => void;
    multiSelect?: boolean;
    hasShowMore?: boolean;
    isLocalFile?: boolean;
    fileRemoved?: () => void;
    itemSelected?: (item: any) => void;
    isBusy?: boolean;
    nonClickable?: boolean;
}

const FileviewComponent = ({
    item,
    exchangeItem,
    isMobile,
    tenantEnablePII = false,
    selectFile,
    selectExchangeItem,
    openFileOptions,
    fileRemoved,
    itemSelected,
    isBusy = false,
    multiSelect = true,
    hasShowMore = true,
    isLocalFile = false,
    nonClickable = false
}: IFileviewProps) => {

    const languageStrings = React.useContext(LanguageContext);
    const [modifiedBy, setModifiedBy] = React.useState<string>();

    const currentDirectory = useSelector((state: RootState) =>
        state.workingDirectories?.workingDirectories?.length > 0
            ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1]
            : null
    );

    const removableAndNonClickable: boolean = React.useMemo(() => isLocalFile && !!fileRemoved, [isLocalFile, fileRemoved]);
    const isNonMultiLayout = React.useMemo(() => isMobile || !multiSelect, [isMobile, multiSelect]);

    const initModifiedBy = React.useCallback(async (item: IFile) => {
        let modifiedByValue: string;
        if (item.modifiedBy) {
            modifiedByValue = item.modifiedBy;
        }
        else if (item['Modified By']) {
            modifiedByValue = item['Modified By'];
        }
        else if (item['Editor']?.length > 0) {
            modifiedByValue = item['Editor'][0]['title'];
        }
        else if (item["modifiedby"]?.length > 0) {
            modifiedByValue = item["modifiedby"];
        }
        else if (item["modified by"]?.length > 0) {
            modifiedByValue = item["modified by"];
        }
        else if (item.driveId && item.id) {
            const graphItemData = await getItemCustomSelect(await authModule.getGraphAccessToken(), item.driveId, item.id, "lastModifiedBy");
            modifiedByValue = graphItemData?.lastModifiedBy?.user?.displayName ?? languageStrings.Unknown;
        }
        else {
            modifiedByValue = languageStrings.Unknown;
        }

        setModifiedBy(modifiedByValue);
    }, [languageStrings]);

    React.useEffect(() => {
        if (!isLocalFile && currentDirectory?.type !== ComponentTypes.SharePointList) {
            initModifiedBy(item);
        }
    }, [item, isLocalFile, currentDirectory, initModifiedBy]);

    const fileItemClass = React.useMemo(() => {
        if (nonClickable) {
            return 'list-item non-clickable';
        }

        if (removableAndNonClickable) {
            return 'list-item removeable';
        }

        if (!tenantEnablePII) {
            return 'list-item';
        }

        if (tenantEnablePII) {
            if (item.pii?.piiError) {
                return 'pii-error';
            }
            if (!item.pii ||
                (item.pii.pii === null && item.pii.piiDetected === null) ||
                (item.pii.pii === null && item.pii.piiDetected === false) ||
                (item.pii.pii === undefined && item.pii.piiDetected === false) ||
                item.pii?.piiError
            ) {
                return 'list-item';
            }
            return item.pii.piiDetected === true ? 'pii-detected' : 'pii-not-detected';
        }
        return 'list-item';
    }, [item, tenantEnablePII, removableAndNonClickable, nonClickable]);

    const fileItemIcon = React.useMemo(() => {
        if (!item) return null;
        if (isBusy) return <Spinner size={SpinnerSize.small} />;

        if (currentDirectory?.type === ComponentTypes.SharePointList) {
            return <Icon
                {...getFileTypeIconProps({
                    type: FileIconType.listItem,
                    size: 20,
                    imageFileType: "svg",
                })}
            />
        }

        if (item["FSObjType"] === '1' && item["ProgId"] !== OneNoteProgId) {
            return <Icon {...getFileTypeIconProps({ type: item["ContentTypeId"]?.startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />
        }

        if (item["FSObjType"] === '1' && item["ProgId"] === OneNoteProgId) {
            return <Icon {...getFileTypeIconProps({ extension: 'onetoc', size: 20, imageFileType: "svg" })} styles={iconStyles} />
        }

        if (item["contentTypeId"]?.startsWith(SharePointFolderContentTypeIds.folder)) {
            return <Icon {...getFileTypeIconProps({ type: item["contentTypeId"].startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />
        }

        if ((item as any).currentLocation?.contentTypeId && item["currentLocation"]["contentTypeId"]?.startsWith(SharePointFolderContentTypeIds.folder)) {
            return <Icon {...getFileTypeIconProps({ type: item["currentLocation"]["contentTypeId"].startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />
        }

        const itemExt: string = item["extension"] || getExtension(item?.name);
        if (itemExt) {
            return <Icon {...getFileTypeIconProps({ extension: itemExt, size: 20, imageFileType: "svg", })} styles={iconStyles} />
        }

        // Advanced search item
        if ((item as any)?.contenttypeid && item?.name) {
            if (!item["contenttypeid"].startsWith(SharePointFolderContentTypeIds.folder)) {
                let extension: string;
                const fileParts = item.name?.split('.');
                if (fileParts?.length > 0) {
                    extension = fileParts[fileParts.length - 1];
                }

                return <Icon {...getFileTypeIconProps({ extension: extension, size: 20, imageFileType: "svg", })} styles={iconStyles} />
            }
            else {
                return <Icon {...getFileTypeIconProps({ type: item["contenttypeid"].startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />
            }
        }

        // Advanced search recent item
        if ((item as any)?.sizetext) {
            if ((item as any)?.isdocument) {
                let extension: string;
                const fileParts = item.name?.split('.');
                if (fileParts?.length > 0) {
                    extension = fileParts[fileParts.length - 1];
                }

                return <Icon {...getFileTypeIconProps({ extension: extension, size: 20, imageFileType: "svg", })} styles={iconStyles} />
            }
            else {
                return <Icon {...getFileTypeIconProps({ type: FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />
            }
        }

        if (!tenantEnablePII || !item.pii) {
            return <Icon styles={multiSelect ? iconStyles : null}
                {...getFileTypeIconProps({
                    extension: item.fileType || item['Type'] || item['extension'] || getExtension(item[sharePointInternalFileName]),
                    size: isNonMultiLayout ? 20 : 16,
                    imageFileType: "svg",
                })}
            />;
        }

        if (item.pii.isChecking) {
            return <TooltipHost content={languageStrings.PrivacySensitiveChecking}>
                {multiSelect ? <Spinner size={SpinnerSize.xSmall} /> : <Spinner size={SpinnerSize.small} />}
            </TooltipHost>;
        }

        if (!item.pii.piiError
            && ((item.pii?.pii === null && item.pii?.piiDetected === null)
                || (item.pii?.pii === null && item.pii?.piiDetected === false)
                || (item.pii?.pii === undefined && item.pii?.piiDetected === false))) {

            return <TooltipHost content={languageStrings.PrivacySensitiveDataNotChecked}>
                <Icon styles={multiSelect ? iconStyles : null}
                    {...getFileTypeIconProps({
                        extension: item.fileType || item['Type'] || item['extension'] || getExtension(item[sharePointInternalFileName]),
                        size: isNonMultiLayout ? 20 : 16,
                        imageFileType: "svg",
                    })}
                />
            </TooltipHost>;
        }

        return <TooltipHost
            content={<PiiTooltip PIIDetected={item.pii?.piiDetected} PII={item.pii?.pii} piiError={item.pii?.piiError} />}
            calloutProps={{ gapSpace: 0, calloutWidth: 250 }}
            styles={{
                root: {
                    display: "inline-block",
                    paddingTop: 3
                },
            }}
            delay={TooltipDelay.medium}
        >
            <Icon styles={multiSelect ? iconStyles : null}
                {...getFileTypeIconProps({
                    extension: item.fileType || item['Type'] || item['extension'],
                    size: isNonMultiLayout ? 20 : 16,
                    imageFileType: "svg",
                })}
            />
        </TooltipHost>;
    }, [item, isBusy, currentDirectory, multiSelect, isNonMultiLayout, tenantEnablePII, languageStrings]);

    const fileName = React.useMemo(() => {
        if (!item) return "";
        if (currentDirectory?.type === ComponentTypes.SharePointList) return item["Title"] || "";
        return item.name || item["Name"] || item[sharePointInternalFileName] || "";
    }, [item, currentDirectory]);

    const handleSelectFile = React.useCallback(() => {
        selectFile?.(item);
    }, [item, selectFile]);

    const handleFileClick = React.useCallback(() => {
        if (nonClickable || removableAndNonClickable) return;
        openFileOptions?.(item);
        itemSelected?.(item);
    }, [item, openFileOptions, itemSelected, nonClickable, removableAndNonClickable]);

    const handleRemoveFile = React.useCallback(() => {
        fileRemoved?.();
    }, [fileRemoved]);

    const handleMoreOptionsClick = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        openFileOptions?.(item);
    }, [item, openFileOptions]);

    const handleSelectExchangeItem = React.useCallback(() => {
        selectExchangeItem?.(exchangeItem);
    }, [exchangeItem, selectExchangeItem]);

    return (
        <>
            {item && <div className={fileItemClass} style={isMobile ? mobileListItemStyle : {}}>
                {multiSelect && !isMobile && <div style={{ paddingRight: "1px" }} onClick={handleSelectFile}>
                    <Check checked={item.selected} />
                </div>}
                <div className="list-item-link" onClick={handleFileClick}>
                    <div className={isNonMultiLayout ? 'list-item-icon' : 'list-item-multi-icon'}>
                        {fileItemIcon}
                    </div>
                    <Text className={isNonMultiLayout ? "list-item-name" : 'list-item-multi-name'}>
                        <TooltipHost content={item.name} overflowMode={TooltipOverflowMode.Parent}>
                            <span style={isNonMultiLayout ? {} : { paddingLeft: "5px" }}>{fileName}</span>
                        </TooltipHost>
                        <div className="xb-grey-text" style={isNonMultiLayout ? {} : { paddingLeft: "5px" }}>
                            {dayjs(item.lastModifiedDateTime).format("l")} {modifiedBy && (languageStrings.By + " " + modifiedBy)}
                        </div>
                        {item.siteDisplayName && <div className="xb-grey-text text-ellipsis" style={isNonMultiLayout ? {} : { paddingLeft: "5px" }}>
                            {item.siteDisplayName}
                        </div>}
                    </Text>
                </div>
                {removableAndNonClickable && <div className="list-item-remove" onClick={handleRemoveFile} >
                    <FontIcon iconName="Cancel" />
                </div>}
                {hasShowMore && <TooltipHost content={languageStrings.MoreActions}>
                    <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                        {/* <FontIcon iconName="More" onClick={handleMoreOptionsClick} /> */}
                        <MoreVertical20Regular onClick={handleMoreOptionsClick} />
                    </div>
                </TooltipHost>}
            </div>}
            {exchangeItem &&
                <div className="list-item" style={isMobile ? mobileListItemStyle : {}}>
                    {multiSelect && !isMobile && <div style={{ paddingRight: "1px" }} onClick={handleSelectExchangeItem}>
                        <Check checked={exchangeItem.selected} />
                    </div>}
                    <div className="list-item-link">
                        <div className={isNonMultiLayout ? 'list-item-icon' : 'list-item-multi-icon'}>
                            <Icon
                                {...getFileTypeIconProps({
                                    extension: "eml",
                                    size: isNonMultiLayout ? 20 : 16,
                                    imageFileType: "svg",
                                })}
                            />
                        </div>
                        <Text className={isNonMultiLayout ? "list-item-name" : 'list-item-multi-name'}>
                            <TooltipHost content={exchangeItem.subject} overflowMode={TooltipOverflowMode.Parent}>
                                <span style={isNonMultiLayout ? {} : { paddingLeft: "5px" }}>{exchangeItem.subject}</span>
                            </TooltipHost>
                            <div className="xb-grey-text" style={multiSelect ? { paddingLeft: "5px" } : {}}>
                                {`${dayjs(exchangeItem.receivedDateTime).format("l")} ${languageStrings.From} ${exchangeItem.sender.emailAddress.name}`}
                            </div>
                        </Text>
                    </div>
                    {exchangeItem.hasAttachments && <div className="list-item-attach">
                        <FontIcon iconName="Attach" />
                    </div>}
                </div>
            }
        </>
    )
};

export const Fileview = React.memo(FileviewComponent);